import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import { IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { MUIDataTableOptions } from 'mui-datatables';
import React from 'react';
import { DateTimeFormat, getLocalDateTime } from '../../../common';
import { ClientSideDataTable } from '../../../common/components/dataTable/clientSide/clientSideDataTable';
import { DatatableColumn, FilterDataType } from '../../../common/components/dataTable/types';
import { LoadingProgress } from '../../../common/store/types';
import { EntityAttachmentItem } from '../store/types';

export interface Props {
  data: EntityAttachmentItem[];
  loadingProgress: LoadingProgress;
  onDownload: (item: EntityAttachmentItem) => void;
  onDelete: (id: number) => void;
}

export const useStyles = makeStyles(() => ({
  datatable: {
    '& .MuiTable-root': {
      marginTop: '0 !important',
    },

    '& .MuiToolbar-root': {
      '& > div:first-of-type': {
        display: 'none',
      },
    },
  },
}));

export function ExportsTable({ data, loadingProgress, onDownload, onDelete }: Props): JSX.Element {
  const classes = useStyles();
  const customTableHeaderOptions = {
    sort: true,
  };

  const columns: DatatableColumn[] = [
    {
      filterDataType: FilterDataType.string,
      label: 'AS AT DATE',
      name: 'issueDate',
      options: {
        ...customTableHeaderOptions,
        customBodyRenderLite: function customBodyRenderLite(dataIndex: number): React.ReactNode {
          return <Typography variant={'h5'}>{getLocalDateTime(data[dataIndex]?.issueDate ?? undefined, DateTimeFormat.Short)}</Typography>;
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      label: 'NAME',
      name: 'name',
      options: {
        ...customTableHeaderOptions,
        customBodyRenderLite: function customBodyRenderLite(dataIndex: number): React.ReactNode {
          return <Typography variant={'h5'}>{data[dataIndex]?.name}</Typography>;
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      label: 'TYPE',
      name: 'type',
      options: {
        ...customTableHeaderOptions,
        customBodyRenderLite: function customBodyRenderLite(dataIndex: number): React.ReactNode {
          return <Typography variant={'h5'}>{data[dataIndex]?.type}</Typography>;
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      label: 'FILE NAME',
      name: 'fileName',
      options: {
        ...customTableHeaderOptions,
        customBodyRenderLite: function customBodyRenderLite(dataIndex: number): React.ReactNode {
          return <Typography variant={'h5'}>{data[dataIndex]?.fileName}</Typography>;
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      label: 'GENERATED DATETIME',
      name: 'dateCreated',
      options: {
        ...customTableHeaderOptions,
        customBodyRenderLite: function customBodyRenderLite(dataIndex: number): React.ReactNode {
          return <Typography variant={'h5'}>{getLocalDateTime(data[dataIndex]?.dateCreated, DateTimeFormat.DateTime)}</Typography>;
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      label: ' ',
      name: 'actionsColumn',
      options: {
        sort: false,
        filter: false,
        display: true,
        customBodyRenderLite: function customBodyRenderLite(dataIndex: number): React.ReactNode {
          return (
            <>
              <IconButton onClick={() => onDownload(data[dataIndex])} color="primary" size="small" style={{ marginRight: '8px' }}>
                <DownloadOutlined />
              </IconButton>
              <IconButton onClick={() => onDelete(data[dataIndex].id)} color="error" size="small">
                <DeleteOutlined />
              </IconButton>
            </>
          );
        },
      },
    },
  ];

  const options: MUIDataTableOptions = {
    elevation: 5,
    filter: false,
    print: false,
    download: false,
    viewColumns: false,
    selectableRows: 'none',
    responsive: 'standard',
    pagination: true,
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 25, 50],
  };

  return (
    <div className={classes.datatable}>
      <ClientSideDataTable title="" data={data} columns={columns} options={options} loadingProgress={loadingProgress} />
    </div>
  );
}
